import { Link } from 'react-router-dom';
import { Users, Calendar, BookOpen } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Card from '../common/Card';
import Badge from '../common/Badge';
import Button from '../common/Button';
import { formatDate } from '../../utils/formatters';
import storageService from '../../services/storage';

const GroupCard = ({ group, isAdmin = false, onJoin }) => {
  const { t, i18n } = useTranslation();
  const linkPath = isAdmin ? `/admin/groups/${group.id}` : `/groups/${group.id}`;

  const getStatusVariant = (statut) => {
    switch (statut) {
      case 'actif':
        return 'success';
      case 'inactif':
        return 'warning';
      case 'archive':
        return 'default';
      default:
        return 'default';
    }
  };

  return (
    <Card hover className="h-full flex flex-col">
      {/* Group Image */}
      <Link to={linkPath} className="block group">
        <div className="relative h-48 mb-4 overflow-hidden rounded-lg bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-900 dark:to-primary-800">
          {group.image_couverture ? (
            <img
              src={storageService.getImageUrl(group.image_couverture)}
              alt={group.nom}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Users className="h-16 w-16 text-primary-500 opacity-50" />
            </div>
          )}

          <div className="absolute top-2 right-2">
            <Badge variant={getStatusVariant(group.statut)}>
              {t(`group_status.${group.statut}`, group.statut)}
            </Badge>
          </div>
        </div>
      </Link>

      {/* Group Info */}
      <div className="space-y-3 flex-grow mb-6">
        {/* Theme Badge */}
        <div className="flex justify-between items-start">
          <Badge variant="primary" size="sm">
            {group.theme}
          </Badge>
        </div>

        {/* Title */}
        <Link to={linkPath}>
          <h3 className="font-semibold text-gray-900 dark:text-white text-lg line-clamp-2 hover:text-primary-600 transition-colors">
            {group.nom}
          </h3>
        </Link>

        {/* Description */}
        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-3">
          {group.description}
        </p>

        {/* Stats */}
        <div className="space-y-2 text-sm">
          <div className="flex items-center text-gray-600 dark:text-gray-400">
            <Users className="h-4 w-4 mr-2" />
            <span>{t('components.group_card.members', { count: group.nombre_membres })}</span>
          </div>

          {group.prochaine_rencontre && (
            <div className="flex items-center text-gray-600 dark:text-gray-400">
              <Calendar className="h-4 w-4 mr-2" />
              <span>{formatDate(group.prochaine_rencontre, 'DD/MM/YYYY', i18n.language)}</span>
            </div>
          )}

          {group.livre_du_mois_titre && (
            <div className="flex items-center text-primary-600 dark:text-primary-400">
              <BookOpen className="h-4 w-4 mr-2" />
              <span className="line-clamp-1 font-medium">{group.livre_du_mois_titre}</span>
            </div>
          )}
        </div>
      </div>

      {/* Action Button */}
      {group.statut === 'actif' && !isAdmin && (
        <Button
          variant="outline"
          fullWidth
          size="sm"
          onClick={(e) => {
            e.preventDefault();
            onJoin && onJoin(group);
          }}
        >
          {t('group_details.join')}
        </Button>
      )}
    </Card>
  );
};

export default GroupCard;
